import { logWarning } from '@databases/logs';
import { type DebouncedFunc, type DebounceSettings, debounce, isEqual } from 'lodash-es';
import type React from 'react';
import {
  type DependencyList,
  type Dispatch,
  type ReactElement,
  type RefObject,
  type SetStateAction
} from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useContext
} from 'react';

import { MenuPortalContext } from './menuPortal.tsx';

/**
 * @deprecated use useCallback() instead.
 * Like useCallback(), but logs and rethrows any sync or async thrown errors.
 */
export function useLoggingCallback<T extends (...args: any[]) => any>(
  callback: T,
  deps: DependencyList
): T {
  /* oxlint-disable react/exhaustive-deps */ // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(
    ((...args: any) => {
      try {
        const result = callback(...args);
        Promise.resolve(result).catch((err) => {
          logWarning(err);
          throw err;
        });
        return result;
      } catch (err: unknown) {
        if (err instanceof Error) {
          logWarning(err);
          throw err;
        }
        return undefined;
      }
    }) as any,
    deps
  );
  /* oxlint-enable react/exhaustive-deps */
}

/**
 * Calls the given callback every time it changes. The callback should be wrapped in useCallback().
 */
export function useCallbackEffect(callback: () => void): void {
  useEffect(() => {
    callback();
  }, [callback]);
}

/**
 * Converts a render function into a memoized function component. The identity of the returned function component
 * never changes, enabling its descendants to be handled properly by React reconciliation.
 *
 * Some AtlasKit components treat render functions as components. For example, <DropdownMenu trigger={() => <Button/>}/>
 * will render <trigger/> rather than {trigger()} to render the trigger. This makes it impossible to use React reconciliation
 * properly. In the example, the button will lose its state every time the dropdown menu is re-rendered, because a new "trigger"
 * component is created on each render. useCallback() only works as a solution if it has no dependencies.
 *
 * Using <DropdownMenu trigger={useComponent(() => <Button>{label}</Button>), [label]}/> is an example how this hook
 * can be used to dynamically create a function component with a fixed identity. The trigger button will be reconciled
 * even if the label changes.
 */
export function useComponent<T extends (props: any) => ReactElement | null>(
  render: T,
  deps: DependencyList
): T {
  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */
  const cb = useRef(render);
  cb.current = render;

  let forceUpdate = () => {};

  useEffect(() => {
    forceUpdate();
  }, [deps]);

  return useCallback(
    ((props) => {
      [, forceUpdate] = useReducer((x) => x + 1, 0);
      return cb.current(props);
    }) as T,
    []
  );
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */
  /* oxlint-enable react/exhaustive-deps, react/rules-of-hooks */
}

/**
 * Like useState(), but if the value specified as override is not undefined, it is returned rather
 * than the current state. This can be used to implement stateless/stateful hybrid components, where
 * a prop overrides the state if it is specified.
 */
export function useStateWithOverride<S>(
  initialState: S | (() => S),
  override: S | undefined
): [S, Dispatch<SetStateAction<S>>] {
  const [state, setState] = useState<S>(initialState);
  if (override === undefined) {
    return [state, setState];
  } else {
    return [override, setState];
  }
}

/**
 * Returns a ref to a boolean that is true as long as the component is mounted.
 */
export function useIsMounted(): RefObject<boolean> {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
}

/**
 * A hook that returns the value during the previous render of the component. If the component is being
 * rendered for the first time, undefined is returned. This can be used for example to compare the previous
 * value of a prop to its current value.
 */
export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

/**
 * Returns a temporary data URL for the given file object. The url is revoked when the file changes or
 * the component is unmounted. If the given file is undefined, undefined is returned.
 */
export function useDataUrl(file: Blob | undefined): string | undefined {
  const dataUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
  useEffect(
    () => () => {
      if (dataUrl) {
        URL.revokeObjectURL(dataUrl);
      }
    },
    [dataUrl]
  );
  return dataUrl;
}

type MouseDownProps = {
  disableOnMouseDown?: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
};

export type HoverProps = Pick<
  React.DOMAttributes<HTMLElement>,
  'onMouseEnter' | 'onMouseLeave'
>;

/**
 * Keep track of whether the mouse cursor is inside a particular element or not. Apply the hoverProps to the element.
 */
export function useHoverState(
  {
    onMouseEnter,
    onMouseLeave,
    disableOnMouseDown,
    onMouseDown
  }: Partial<HoverProps> & Partial<MouseDownProps> = {
    disableOnMouseDown: false
  }
): [
  isHover: boolean,
  hoverProps: HoverProps,
  setIsHover: Dispatch<SetStateAction<boolean>>
] {
  const [isHover, setIsHover] = useState(false);

  const props = useMemo((): HoverProps => {
    const hoverProps: HoverProps = {
      onMouseEnter: (e) => {
        setIsHover(true);
        onMouseEnter?.(e);
      },
      onMouseLeave: (e) => {
        setIsHover(false);
        onMouseLeave?.(e);
      }
    };
    const mouseDownProps: MouseDownProps = {
      onMouseDown: (e) => {
        setIsHover(false);
        onMouseDown?.(e);
      }
    };

    return {
      ...hoverProps,
      ...(disableOnMouseDown ? mouseDownProps : {})
    };
  }, [disableOnMouseDown, onMouseDown, onMouseEnter, onMouseLeave]);

  return [isHover, props, setIsHover];
}

export type FocusProps = Pick<React.DOMAttributes<HTMLElement>, 'onFocus' | 'onBlur'>;

/**
 * Keep track of whether the focus is inside a particular element (or its descendants) or not. Apply the focusProps to the element.
 */
export function useFocusState({ onFocus, onBlur }: Partial<FocusProps> = {}): [
  isFocus: boolean,
  focusProps: FocusProps
] {
  const [isFocus, setIsFocus] = useState(false);

  const props = useMemo(
    (): FocusProps => ({
      onFocus: (e) => {
        setIsFocus(true);
        onFocus?.(e);
      },
      onBlur: (e) => {
        setIsFocus(false);
        onBlur?.(e);
      }
    }),
    [onBlur, onFocus]
  );

  return [isFocus, props];
}

/**
 * Registers a resize observer on the given HTML element and calls the given callback function whenever the dimensions of the given
 * element change. The callback is called immediately the first time the hook is called, unless the element is invisible
 * (see https://stackoverflow.com/a/60026394/242365).
 */
export function useResizeObserverCallback(
  el: HTMLElement[] | HTMLElement | undefined | null,
  handleResize: ResizeObserverCallback,
  box?: ResizeObserverOptions['box']
): ResizeObserver {
  const callbackRef = useRef<ResizeObserverCallback>(handleResize);
  callbackRef.current = handleResize;

  const timeoutIdsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const observerRef = useRef(
    new ResizeObserver((...args) => {
      // Call asynchronously to avoid "ResizeObserver loop limit exceeded" errors
      const timeoutId = setTimeout(() => {
        timeoutIdsRef.current.delete(timeoutId);
        callbackRef.current(...args);
      }, 0);
      timeoutIdsRef.current.add(timeoutId);
    })
  );

  const prevEls = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const els = Array.isArray(el) ? el : el ? [el] : [];
    for (const el of prevEls.current) {
      if (!els.includes(el)) {
        observerRef.current.unobserve(el);
      }
    }
    for (const el of els) {
      observerRef.current.observe(el, { box });
    }
    prevEls.current = els;
  }, [box, el]);

  useEffect(() => {
    const observer = observerRef.current;
    const timeoutIds = timeoutIdsRef.current;
    return () => {
      observer.disconnect();
      for (const timeoutId of timeoutIds) {
        clearTimeout(timeoutId);
      }
      timeoutIds.clear();
    };
  }, []);

  return observerRef.current;
}

/**
 * Registers a resize observer on the given HTML element, returns the dimensions of that element, and forces the
 * current component to rerender whenever the dimensions of the given element change.
 * @param el The given HTML element.
 * @param useClientSize If true, use clientWidth/clientHeight.
 * @param useBoundingClientRect If true, use getBoundingClientRect. If false, use offsetWidth/offsetHeight.
 */
export function useResizeObserver(
  el: HTMLElement | undefined | null,
  useClientSize = false,
  useBoundingClientRect = false
): { width: number; height: number } | undefined {
  const [size, setSize] = useState<{ width: number; height: number }>();

  const handleResize = useCallback(() => {
    const newSize = el
      ? {
          width: useClientSize
            ? el.clientWidth
            : useBoundingClientRect
              ? el?.getBoundingClientRect().width
              : el.offsetWidth,
          height: useClientSize
            ? el.clientHeight
            : useBoundingClientRect
              ? el?.getBoundingClientRect().height
              : el.offsetHeight
        }
      : undefined;
    if (!isEqual(size, newSize)) {
      setSize(newSize);
    }
  }, [el, size, useBoundingClientRect, useClientSize]);

  useResizeObserverCallback(el, handleResize, 'border-box');

  return size;
}

/**
 * Returns a ref callback and max height for content that should fit below its
 * rendered position in the viewport.
 */
export function useViewportBoundedMaxHeight<T extends HTMLElement>({
  maxHeight,
  viewportMargin = 0,
  measureAfterAnimationFrame = false
}: {
  maxHeight?: number;
  viewportMargin?: number;
  measureAfterAnimationFrame?: boolean;
} = {}): [(element: T | null) => void, number | undefined] {
  const elementRef = useRef<T | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const [viewportMaxHeight, setViewportMaxHeight] = useState<number | undefined>();

  const updateViewportMaxHeight = useCallback(() => {
    if (typeof window === 'undefined' || !elementRef.current) {
      return;
    }

    const { top } = elementRef.current.getBoundingClientRect();
    const availableHeight = Math.max(0, window.innerHeight - top - viewportMargin);
    const nextMaxHeight =
      maxHeight === undefined ? availableHeight : Math.min(maxHeight, availableHeight);

    setViewportMaxHeight((currentMaxHeight) =>
      currentMaxHeight === nextMaxHeight ? currentMaxHeight : nextMaxHeight
    );
  }, [maxHeight, viewportMargin]);

  const scheduleViewportMaxHeightUpdate = useCallback(() => {
    updateViewportMaxHeight();

    if (typeof window === 'undefined' || !measureAfterAnimationFrame) {
      return;
    }

    if (animationFrameRef.current !== undefined) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = undefined;
      updateViewportMaxHeight();
    });
  }, [measureAfterAnimationFrame, updateViewportMaxHeight]);

  const setElement = useCallback(
    (element: T | null) => {
      elementRef.current = element;
      scheduleViewportMaxHeightUpdate();
    },
    [scheduleViewportMaxHeightUpdate]
  );

  useLayoutEffect(() => {
    scheduleViewportMaxHeightUpdate();

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', scheduleViewportMaxHeightUpdate);
    return () => {
      window.removeEventListener('resize', scheduleViewportMaxHeightUpdate);
      if (animationFrameRef.current !== undefined) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [scheduleViewportMaxHeightUpdate]);

  return [setElement, viewportMaxHeight];
}

export const modifiers = ['ctrlKey', 'metaKey', 'shiftKey', 'altKey'] as const;
export type KeyboardShortcut = {
  modifiers?: ReadonlyArray<(typeof modifiers)[any]>;
  code: KeyboardEvent['code'];
};

/**
 * Returns true if any of the Ctrl, Alt, Meta or Shift keys was pressed while the given event was fired.
 */
export function isModifierKeyPressed(
  event: {
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
  },
  selectedModifiers?: ReadonlyArray<(typeof modifiers)[any]>
): boolean {
  if (selectedModifiers) {
    return selectedModifiers.every((modifier) => !!event[modifier]);
  } else {
    return event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
  }
}

// Intercept global undo when a text input is focused
function isEditingTextInput(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement || !(activeElement instanceof HTMLElement)) {
    return false;
  }
  const tagName = activeElement.tagName.toLowerCase();
  // Exclude checkboxes — a focused checkbox (e.g. row-selection checkbox)
  // should not block undo/redo keyboard shortcuts.
  if (tagName === 'input' && (activeElement as HTMLInputElement).type === 'checkbox') {
    return false;
  }
  return tagName === 'input' || tagName === 'textarea' || activeElement.isContentEditable;
}

function isUndoRedoEvent(event: KeyboardEvent): boolean {
  if (event.code !== 'KeyZ') {
    return false;
  }
  const isApple = !!navigator.platform.match(/iphone|ipad|ipod|mac/i);
  const hasCtrlOrMeta = event.ctrlKey || event.metaKey;
  return hasCtrlOrMeta && (isApple ? event.metaKey : event.ctrlKey);
}

export function registerKeyboardShortcut(
  shortcut: KeyboardShortcut | KeyboardShortcut[],
  callback: () => void,
  preventDefault = true
): () => void {
  const listener = (e: KeyboardEvent) => {
    if (
      (Array.isArray(shortcut) ? shortcut : [shortcut]).some(
        (s) =>
          e.code === s.code &&
          modifiers.every((m) => !!e[m] === !!s.modifiers?.includes(m))
      )
    ) {
      if (isEditingTextInput() && isUndoRedoEvent(e)) {
        return;
      }
      if (preventDefault) {
        e.preventDefault();
      }
      callback();
    }
  };
  document.addEventListener('keydown', listener);
  return () => {
    document.removeEventListener('keydown', listener);
  };
}

/**
 * Listen to keyDownEvent and execute callback after matching the commands with the keys.
 */
export function useKeyboardShortcut(
  shortcut: KeyboardShortcut | KeyboardShortcut[] | undefined,
  callback: () => void,
  preventDefault = true
): void {
  useEffect(
    () => registerKeyboardShortcut(shortcut ?? [], callback, preventDefault),
    [callback, preventDefault, shortcut]
  );
}

/**
 * Focuses the first input field (input, textarea, select) that is a descendant of the given container.
 * If the container does not contain an input field, a mutation observer is registered that will focus the first input field as soon
 * as one appears. This makes it possible to focus the input field if it is rendered as part of a lazy component.
 */
export function useFocusInputField(
  container: HTMLElement | null | undefined | (() => HTMLElement | null | undefined),
  selector = 'input:not([type]),input[type=text],textarea,select'
): void {
  const focus = useCallback(() => {
    const resolvedContainer = typeof container === 'function' ? container() : container;
    const inputEl = resolvedContainer?.matches(selector)
      ? resolvedContainer
      : resolvedContainer?.querySelector<HTMLElement>(selector);
    inputEl?.focus();
    return !!inputEl;
  }, [container, selector]);

  useEffect(() => {
    const resolvedContainer = typeof container === 'function' ? container() : container;
    if (resolvedContainer) {
      if (!focus()) {
        const observer = new MutationObserver(() => {
          if (focus()) {
            observer.disconnect();
          }
        });
        observer.observe(resolvedContainer, { subtree: true, childList: true });
        return () => {
          observer.disconnect();
        };
      }
    }
  }, [container, focus]);
}

/**
 * Returns a debounced version of the provided callback. If the provided callback changes while a call is being delayed, the
 * new version of the callback will be called after the delay.
 */
export function useDebouncedCallback<Args extends any[]>(
  callback: (...args: Args) => void,
  waitMs: number,
  options?: DebounceSettings
): DebouncedFunc<(...args: Args) => void> {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const hasOptions = options != null;
  const { leading, maxWait, trailing } = options ?? {};
  const debounceOptions = useMemo<DebounceSettings | undefined>(() => {
    if (!hasOptions) {
      return undefined;
    }

    return {
      ...(leading === undefined ? {} : { leading }),
      ...(maxWait === undefined ? {} : { maxWait }),
      ...(trailing === undefined ? {} : { trailing })
    };
  }, [hasOptions, leading, maxWait, trailing]);

  const debouncedCallback = useMemo(
    () =>
      debounce(
        (...args: Args) => {
          callbackRef.current(...args);
        },
        waitMs,
        debounceOptions
      ),
    [waitMs, debounceOptions]
  );

  useEffect(() => () => debouncedCallback.cancel(), [debouncedCallback]);

  return debouncedCallback;
}

/**
 * Returns a dynamic map of ref objects. When a ref object from the map is accessed which hasn't been accessed before,
 * it is automatically created (through a proxy).
 * @param onUpdate A handler that is called when a ref value is updated
 */
export function useRefMap<T>({
  onUpdate
}: {
  onUpdate?: (oldValue: T | null, newValue: T | null) => void;
} = {}): Record<string | symbol, RefObject<T>> {
  const refs = useRef<Record<string | symbol, T | null>>({});
  const refWrappers = useRef(
    new Proxy<Record<string | symbol, RefObject<T>>>(
      {},
      {
        get(obj, prop) {
          if (!obj[prop]) {
            refs.current[prop] = null;
            obj[prop] = {
              get current() {
                return refs.current[prop];
              },
              set current(newValue) {
                const oldValue = refs.current[prop];
                refs.current[prop] = newValue;
                onUpdate?.(oldValue, newValue);
              }
            };
          }

          return obj[prop];
        }
      }
    )
  );
  return refWrappers.current;
}

/**
 * Creates a custom hook that keeps track of whether a click event occurred outside the given element.
 * If true, the callback is called.
 */
export function useOnClickOutsideRef(
  ref: HTMLElement | null,
  isEnabled: boolean,
  callback: () => void,
  includeMenuPortalTarget = false
): void {
  const menuPortalTarget = useContext(MenuPortalContext);

  useEffect(() => {
    if (!isEnabled) {
      return () => {};
    }
    const handleMouseClick = (event: MouseEvent) => {
      const isChildOfNode = ref?.contains(event.target as Node);

      // include menu portal target for select menus
      const hasMenuPortalTarget = menuPortalTarget?.childNodes?.length;

      if (
        event.target &&
        !event.defaultPrevented &&
        !isChildOfNode &&
        (includeMenuPortalTarget ? !hasMenuPortalTarget : true)
      ) {
        callback();
      }
    };

    // handle click events on the parent window as well
    const parentWindow = window.parent;

    document.addEventListener('click', handleMouseClick);
    if (parentWindow && parentWindow !== window) {
      parentWindow.addEventListener('mousedown', handleMouseClick);
    }

    return () => {
      document.removeEventListener('click', handleMouseClick);
      if (parentWindow && parentWindow !== window) {
        parentWindow.removeEventListener('mousedown', handleMouseClick);
      }
    };
  }, [callback, includeMenuPortalTarget, isEnabled, ref, menuPortalTarget]);
}
